// Shared state between the Messaging page and the new-message popup.
// Module-level on purpose: the popup lives in the Header, the page lives in the
// router outlet, and neither owns the other.
import { updateOwnPresence } from './tabPresence';

// The conversation the user currently has open on /messaging (null = none).
let activeConversationId: number | null = null;
const activeListeners = new Set<(id: number | null) => void>();

export const setActiveConversationId = (id: number | null) => {
    activeConversationId = id;
    updateOwnPresence({ activeConversationId: id });
    activeListeners.forEach((fn) => fn(id));
};
export const subscribeActiveConversation = (fn: (id: number | null) => void) => {
    activeListeners.add(fn);
    return () => {
        activeListeners.delete(fn);
    };
};
export const getActiveConversationId = () => activeConversationId;

// Per-browser mute preferences — a convenience, so localStorage is enough.
export type PopupPrefs = { popupsMuted: boolean; soundMuted: boolean };

const PREFS_KEY = 'message-popup-prefs';
const DEFAULT_PREFS: PopupPrefs = { popupsMuted: false, soundMuted: false };
const listeners = new Set<(prefs: PopupPrefs) => void>();

export const getPopupPrefs = (): PopupPrefs => {
    try {
        return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') };
    } catch {
        return DEFAULT_PREFS;
    }
};

export const setPopupPrefs = (patch: Partial<PopupPrefs>) => {
    const next = { ...getPopupPrefs(), ...patch };
    try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(next));
    } catch {
        // storage blocked — the preference just won't survive a reload
    }
    listeners.forEach((fn) => fn(next));
};

// Muting in one tab applies to the other open tabs too.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
        if (e.key === PREFS_KEY) {
            const prefs = getPopupPrefs();
            listeners.forEach((fn) => fn(prefs));
        }
    });
}

export const subscribePopupPrefs = (fn: (prefs: PopupPrefs) => void) => {
    listeners.add(fn);
    return () => {
        listeners.delete(fn);
    };
};
